import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, CalendarCheck, CalendarPlus } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RegistrationForm = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role: "",
    referred_by: "",
    class_id: "buyers-roadmap",
  });
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [joinLink, setJoinLink] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/classes`)
      .then((res) => setClasses(res.data.filter((c) => c.type !== "Test")))
      .catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/register`, form);
      setJoinLink(res.data?.join_link || null);
      setSuccess(true);
      toast.success("You're registered! Check your email for details.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        data-testid="reg-form-success"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="bg-white rounded-[2rem] p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E7E5E4] text-center"
      >
        <div className="mx-auto w-16 h-16 rounded-2xl bg-[#F4EBE1] flex items-center justify-center mb-6">
          <CheckCircle2 className="w-8 h-8 text-[#D97757]" />
        </div>
        <h3 className="font-display text-2xl font-medium text-[#1C1917] mb-3">
          You're on the list, {form.first_name}!
        </h3>
        <p className="text-[#57534E] leading-relaxed mb-6">
          A confirmation with your webinar link and calendar invite is on its way to{" "}
          <span className="font-semibold text-[#1C1917]">{form.email}</span>.
        </p>

        {joinLink && (
          <a
            data-testid="reg-form-join-link"
            href={joinLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 h-12 rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold text-sm mb-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
          >
            <CalendarCheck className="w-4 h-4" /> Your personal join link
          </a>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <a
            data-testid="reg-form-ics-weekly"
            href={`${API}/calendar/weekly.ics`}
            className="flex items-center justify-center gap-2 h-11 rounded-xl bg-[#1C1917] text-white font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
          >
            <CalendarPlus className="w-4 h-4" /> Add Weekly Class
          </a>
          <a
            data-testid="reg-form-ics-hot-topic"
            href={`${API}/calendar/hot-topic.ics`}
            className="flex items-center justify-center gap-2 h-11 rounded-xl bg-[#F4EBE1] text-[#1C1917] font-semibold text-sm border border-[#E7E5E4] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
          >
            <CalendarPlus className="w-4 h-4 text-[#D97757]" /> Add Hot Topic
          </a>
        </div>

        <div className="flex items-center justify-center gap-2 text-sm text-[#57534E]">
          <CalendarCheck className="w-4 h-4 text-[#D97757]" />
          Added to your calendar — feel free to invite a client!
        </div>
      </motion.div>
    );
  }

  return (
    <div
      data-testid="reg-form-card"
      className="bg-white rounded-[2rem] p-7 md:p-9 shadow-[0_18px_50px_rgb(0,0,0,0.10)] border border-[#E7E5E4]"
    >
      <div className="mb-6">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D97757]">
          Free Registration
        </span>
        <h3 className="font-display text-2xl font-medium text-[#1C1917] mt-2">
          Save your seat
        </h3>
        <p className="text-sm text-[#57534E] mt-1">
          Reserve a spot for you — or invite a client.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="first_name" className="text-sm font-medium text-[#1C1917]">
              First name
            </Label>
            <Input
              id="first_name"
              data-testid="reg-form-first-name"
              value={form.first_name}
              onChange={(e) => update("first_name", e.target.value)}
              placeholder="Jane"
              className="mt-1.5 rounded-xl border-[#E7E5E4] focus-visible:ring-[#D97757] h-11"
            />
          </div>
          <div>
            <Label htmlFor="last_name" className="text-sm font-medium text-[#1C1917]">
              Last name
            </Label>
            <Input
              id="last_name"
              data-testid="reg-form-last-name"
              value={form.last_name}
              onChange={(e) => update("last_name", e.target.value)}
              placeholder="Doe"
              className="mt-1.5 rounded-xl border-[#E7E5E4] focus-visible:ring-[#D97757] h-11"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email" className="text-sm font-medium text-[#1C1917]">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            data-testid="reg-form-email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="jane@email.com"
            className="mt-1.5 rounded-xl border-[#E7E5E4] focus-visible:ring-[#D97757] h-11"
          />
        </div>

        <div>
          <Label htmlFor="phone" className="text-sm font-medium text-[#1C1917]">
            Phone <span className="text-[#A8A29E] font-normal">(optional)</span>
          </Label>
          <Input
            id="phone"
            data-testid="reg-form-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="(555) 123-4567"
            className="mt-1.5 rounded-xl border-[#E7E5E4] focus-visible:ring-[#D97757] h-11"
          />
        </div>

        <div>
          <Label htmlFor="referred_by" className="text-sm font-medium text-[#1C1917]">
            Referred by <span className="text-[#A8A29E] font-normal">(optional)</span>
          </Label>
          <Input
            id="referred_by"
            data-testid="reg-form-referred-by"
            value={form.referred_by}
            onChange={(e) => update("referred_by", e.target.value)}
            placeholder="Who invited you? (agent or friend's name)"
            className="mt-1.5 rounded-xl border-[#E7E5E4] focus-visible:ring-[#D97757] h-11"
          />
        </div>

        <div>
          <Label className="text-sm font-medium text-[#1C1917]">Which class?</Label>
          <Select value={form.class_id} onValueChange={(v) => update("class_id", v)}>
            <SelectTrigger
              data-testid="reg-form-class"
              className="mt-1.5 rounded-xl border-[#E7E5E4] focus:ring-[#D97757] h-11"
            >
              <SelectValue placeholder="Choose a class" />
            </SelectTrigger>
            <SelectContent>
              {classes.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label} · {c.day}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-sm font-medium text-[#1C1917]">I am a...</Label>
          <Select value={form.role} onValueChange={(v) => update("role", v)}>
            <SelectTrigger
              data-testid="reg-form-role"
              className="mt-1.5 rounded-xl border-[#E7E5E4] focus:ring-[#D97757] h-11"
            >
              <SelectValue placeholder="Select one" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="agent">Real Estate Agent</SelectItem>
              <SelectItem value="client">Buyer / Client</SelectItem>
              <SelectItem value="partner">Industry Partner</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          type="submit"
          data-testid="reg-form-submit"
          disabled={loading}
          className="w-full h-12 rounded-xl bg-[#D97757] hover:bg-[#C26243] text-white font-semibold text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            "Reserve My Seat"
          )}
        </Button>

        <p className="text-xs text-[#A8A29E] text-center leading-relaxed pt-1">
          By registering you'll receive class details and reminders. Sessions hosted via
          Zoho Webinar. No spam — unsubscribe anytime.
        </p>
      </form>
    </div>
  );
};
